/**
 * The password field: a `FormField` plus the reveal toggle.
 *
 * The reveal state is owned HERE, not by the caller. A form has no decision to make about whether
 * the characters are currently visible — it only cares what was typed — so lifting the flag into the
 * screen's state would widen that screen's interface for nothing and make every future caller
 * re-wire the same toggle.
 */

import { useState, type ReactNode, type Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { EyeIcon, EyeOffIcon, LockIcon } from './AuthIcons';
import { FormField } from './FormField';
import styles from './FormField.module.css';

interface PasswordFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoComplete?: string;
  error?: string | null;
  invalid?: boolean;
  disabled?: boolean;
  required?: boolean;
  /** The `<input>`, for a caller that moves focus into it — `/konto`'s password disclosure. */
  inputRef?: Ref<HTMLInputElement>;
}

export function PasswordField({
  label,
  value,
  onChange,
  placeholder,
  autoComplete,
  error = null,
  invalid = false,
  disabled = false,
  required = false,
  inputRef,
}: PasswordFieldProps) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  const toggle: ReactNode = (
    <button
      className={styles.trailingButton}
      type="button"
      // The name states the ACTION the press performs, and `aria-pressed` states where the toggle
      // currently is. Both, because the icon alone conveys neither.
      aria-label={visible ? t('accessibility.hidePassword') : t('accessibility.showPassword')}
      aria-pressed={visible}
      disabled={disabled}
      onClick={() => setVisible((wasVisible) => !wasVisible)}
    >
      {visible ? (
        <EyeOffIcon className={styles.trailingIcon} />
      ) : (
        <EyeIcon className={styles.trailingIcon} />
      )}
    </button>
  );

  return (
    <FormField
      label={label}
      icon={LockIcon}
      type={visible ? 'text' : 'password'}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoComplete={autoComplete}
      error={error}
      invalid={invalid}
      disabled={disabled}
      required={required}
      trailing={toggle}
      inputRef={inputRef}
    />
  );
}
